import { uiPaper } from "shared/ui/uiSurface"
import { Alert, Box, Dialog, Grid, Snackbar, useMediaQuery } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import { FC, useCallback, useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"
import {
  useCreateOrderMutation,
  useGetClientByIdQuery,
  useGetTagsQuery,
} from "../../../apis/appApi"
import { CustomerSection } from "./CustomerSection"
import { OrderCreateDialogActions } from "./OrderCreateDialogActions"
import { OrderCreateDialogHeader } from "./OrderCreateDialogHeader"
import { OrderSection } from "./OrderSection"
import { RecentOrdersSuggestions } from "./RecentOrdersSuggestions"
import { useOrderCreateForm } from "./useOrderCreateForm"

type Props = {
  open: boolean
  onClose: () => void
}

export const OrderCreateDialog: FC<Props> = ({ open, onClose }) => {
  const theme = useTheme()
  const { t } = useTranslation()
  const isMobile = useMediaQuery(theme.breakpoints.down("md"))
  const { state, dispatch, reset } = useOrderCreateForm()
  const [error, setError] = useState<string | null>(null)
  const [filledSnackbar, setFilledSnackbar] = useState<string | null>(null)

  const [createOrder, { isLoading: isSubmitting }] = useCreateOrderMutation()

  // Prefetch tags
  const { data: clientTagsRaw } = useGetTagsQuery({ type: "CLIENT" }, { skip: !open })
  const { data: orderTagsRaw } = useGetTagsQuery({ type: "ORDER" }, { skip: !open })

  const clientTags = useMemo(
    () =>
      (clientTagsRaw ?? []).map((t: any) => ({
        id: t.id,
        label: t.label,
        color: t.color || "#757575",
        icon: t.icon,
        shortLabel: t.shortLabel,
        displayOption: t.displayOption,
      })),
    [clientTagsRaw],
  )

  const orderTags = useMemo(
    () =>
      (orderTagsRaw ?? []).map((t: any) => ({
        id: t.id,
        label: t.label,
        color: t.color || "#757575",
        icon: t.icon,
        shortLabel: t.shortLabel,
        displayOption: t.displayOption,
      })),
    [orderTagsRaw],
  )

  // Load client details when a client is selected
  const { data: clientDetailRes } = useGetClientByIdQuery(state.selectedClient?.id!, {
    skip: !state.selectedClient?.id,
  })

  useEffect(() => {
    if (clientDetailRes?.data) {
      dispatch({ type: "POPULATE_CLIENT", detail: clientDetailRes.data })
    }
  }, [clientDetailRes, dispatch])

  // Reset form when dialog opens
  useEffect(() => {
    if (open) {
      reset()
      setError(null)
      setFilledSnackbar(null)
    }
  }, [open, reset])

  // Validation
  const isValid = useMemo(() => {
    const hasName = state.lastName.trim() !== "" || state.firstName.trim() !== ""
    const selectedAddr = state.addresses[state.selectedAddressIdx]
    const hasAddress = selectedAddr
      ? (!!selectedAddr.id || (selectedAddr.streetName.trim() !== "" && selectedAddr.city.trim() !== ""))
      : false
    // Block submission if MONTHLY recurrence with day > 28
    if (state.recurrenceType === "MONTHLY" && state.deliveryDate.date() > 28) {
      return false
    }
    return hasName && hasAddress && state.selectedClient !== null
  }, [state])

  const handleSubmit = useCallback(async (): Promise<boolean> => {
    setError(null)

    if (!state.selectedClient) {
      setError("Please select an existing client.")
      return false
    }

    const selectedAddr = state.addresses[state.selectedAddressIdx]
    if (!selectedAddr) {
      setError("Please select or enter an address.")
      return false
    }

    // Address must either have an id (existing) or streetName+city (manual entry)
    const hasExistingAddress = !!selectedAddr.id
    const hasManualAddress = selectedAddr.streetName.trim() !== "" && selectedAddr.city.trim() !== ""
    if (!hasExistingAddress && !hasManualAddress) {
      setError("Please select an existing address or enter at least a street name and city.")
      return false
    }

    const initialState = state.workflow === "DELIVERY" ? "CREATED" : "ORDER_CREATED"
    const reference = `ORD-${Date.now()}`

    // Build recurrence object if active
    const recurrence =
      state.recurrenceType !== "NONE"
        ? {
            type: state.recurrenceType as Exclude<typeof state.recurrenceType, "NONE">,
            dailyRecurringCriteria:
              state.recurrenceType === "XDAYS"
                ? state.dailyRecurrenceInterval
                : undefined,
            endDate: state.recurrenceEndDate
              ? state.recurrenceEndDate.format("YYYY-MM-DD")
              : undefined,
          }
        : undefined

    // Source depends on recurrence
    const source = recurrence ? "RECURRENT_CREATED" : "MANUALLY_CREATED"

    // Build address fields — either existing addressId or inline newAddress
    const addressFields = hasExistingAddress
      ? { addressId: selectedAddr.id! }
      : {
          newAddress: {
            streetNumber: selectedAddr.streetNumber,
            streetName: selectedAddr.streetName,
            appartNumber: selectedAddr.appartNumber || null,
            city: selectedAddr.city,
            state: selectedAddr.state || null,
            postcode: selectedAddr.postcode,
            country: selectedAddr.country || null,
            notes: selectedAddr.notes || null,
          },
        }

    // Payment type — send null for "NONE"
    const paymentType = state.paymentMethod !== "NONE" ? state.paymentMethod : null

    try {
      await createOrder({
        clientId: state.selectedClient.id,
        ...addressFields,
        deliveryDate: state.deliveryDate.format("YYYY-MM-DD"),
        desiredArrivalTime: state.desiredArrivalTime,
        desiredArrivalTimeHour: state.desiredArrivalTimeHour || null,
        desiredArrivalTimeHourEnd: state.desiredArrivalTimeHourEnd || null,
        amount: state.amount ? Number(state.amount.replace(/[^0-9.]/g, "")) : null,
        notes: state.orderNotes || null,
        externalReference: state.externalReference || null,
        paymentType,
        reference,
        stateMachineWorkflow: state.workflow,
        source,
        state: initialState,
        recurrence: recurrence ?? null,
        bags: state.bags.length > 0 ? state.bags : undefined,
        prescriptionImages: state.prescriptionImages.length > 0 ? state.prescriptionImages : undefined,
      }).unwrap()
      return true
    } catch (err: any) {
      setError(err?.data?.message || err?.message || "Failed to create order")
      return false
    }
  }, [state, createOrder])

  const handleCreateAndClose = useCallback(async () => {
    const success = await handleSubmit()
    if (success) onClose()
  }, [handleSubmit, onClose])

  const handleCreateAndNew = useCallback(async () => {
    const success = await handleSubmit()
    if (success) {
      reset()
      setError(null)
    }
  }, [handleSubmit, reset])

  return (
    <Dialog
      slotProps={{ paper: uiPaper("UI-DLV2-033") }}
      data-testid="order-create-dialog"
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      fullScreen={isMobile}
    >
      <OrderCreateDialogHeader onClose={onClose} />

      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mx: 3, mt: 2 }}>
          {error}
        </Alert>
      )}

      <Box sx={{ flex: 1, overflow: "auto", px: 3, py: 2 }}>
        <Grid container spacing={3}>
          <Grid size={{ xs: 12, md: 6 }}>
            <CustomerSection state={state} dispatch={dispatch} clientTags={clientTags} />
          </Grid>
          <Grid size={{ xs: 12, md: 6 }}>
            <OrderSection state={state} dispatch={dispatch} orderTags={orderTags} />
          </Grid>
        </Grid>

        {/* Order suggestions — shown when a client is selected */}
        {state.selectedClient && (
          <Box sx={{ mt: 2 }}>
            <RecentOrdersSuggestions
              clientId={state.selectedClient.id}
              addresses={state.addresses}
              dispatch={dispatch}
              onFilled={(ref) => setFilledSnackbar(ref)}
            />
          </Box>
        )}
      </Box>

      <OrderCreateDialogActions
        onCreateAndNew={handleCreateAndNew}
        onCreateAndClose={handleCreateAndClose}
        onCancel={onClose}
        isSubmitting={isSubmitting}
        isValid={isValid}
      />

      <Snackbar
        open={!!filledSnackbar}
        autoHideDuration={3000}
        onClose={() => setFilledSnackbar(null)}
        message={filledSnackbar ? t("orderCreate.recentOrders.filled", { reference: filledSnackbar }) : ""}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      />
    </Dialog>
  )
}
